/* MMX Sticky Add to Cart V1.3.4 — V3 behaviour, lower-cost observer and DOM updates V4 */
(function () {
  'use strict';

  const root = document.querySelector('[data-mmx-sticky-atc]');
  if (!root || root.dataset.mmxSatcInitialised === 'true') return;
  root.dataset.mmxSatcInitialised = 'true';

  const productId = Number(root.getAttribute('data-product-id') || 0);
  const button = root.querySelector('[data-mmx-satc-button]');
  const buttonText = root.querySelector('[data-mmx-satc-button-text]');
  const variantShortNode = root.querySelector('[data-mmx-satc-variant-short]');
  const priceNode = root.querySelector('[data-mmx-satc-price]');
  const compareNode = root.querySelector('[data-mmx-satc-compare]');
  const variantControl = root.querySelector('[data-mmx-satc-variant-control]');
  const variantSelect = root.querySelector('[data-mmx-satc-variant-select]');
  const imageNode = root.querySelector('[data-mmx-satc-image]');
  const deliveryNode = root.querySelector('[data-mmx-satc-delivery]');
  const statusNode = root.querySelector('[data-mmx-satc-status]');
  const productJsonNode = root.querySelector('[data-mmx-satc-product]');

  let productData = null;
  let productForm = null;
  let originalButton = null;
  let originalSelector = null;
  let formObserver = null;
  let documentObserver = null;
  let scrollFrame = 0;
  let syncTimer = 0;
  let nextSyncDue = 0;
  let widgetLayerTimer = 0;
  let widgetLayerFollowups = [];
  let hasTrackedImpression = false;
  let stickyRequestedAdd = false;
  let stickyPendingUntil = 0;
  let forceVariantPriceUntil = 0;
  let wasCartSyncing = false;
  let applyingStickyVariant = false;
  let lastState = '';
  const loweredWidgets = new Set();

  try {
    productData = JSON.parse(productJsonNode ? productJsonNode.textContent : '{}');
  } catch (error) {
    console.error('[MMX Sticky ATC] Invalid product data.', error);
    return;
  }

  const variants = Array.isArray(productData.variants) ? productData.variants : [];
  const optionNames = Array.isArray(productData.options) ? productData.options : [];
  const variantMap = new Map(variants.map((variant) => [Number(variant.id), variant]));

  function boolAttr(name, fallback) {
    const value = root.getAttribute(name);
    if (value == null || value === '') return Boolean(fallback);
    return value === 'true';
  }

  function formatMoney(cents) {
    const format = productData.moneyFormat || (window.theme && window.theme.money_format) || '${{amount}}';
    const value = Number(cents || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return (value / 100).toFixed(2);

    function withDelimiters(number, precision, thousands, decimal) {
      const fixed = (Number(number || 0) / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[0] + (parts[1] ? decimal + parts[1] : '');
    }

    let formatted = '';
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = withDelimiters(value, 2, ',', '.');
    }

    return format.replace(placeholder, formatted);
  }

  function visibleElement(nodes) {
    return Array.from(nodes || []).find((node) => {
      if (!(node instanceof Element)) return false;
      const style = window.getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    }) || null;
  }

  function dispatchInputAndChange(control) {
    if (!control) return;
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function findProductForm() {
    const candidates = Array.from(document.querySelectorAll(
      `form.product-purchase-form[data-product-id="${productId}"], form.product-purchase-form`
    ));

    const matching = candidates.filter((form) => {
      const idField = form.querySelector('[name="id"]');
      const formProductId = Number(form.getAttribute('data-product-id') || 0);
      return formProductId
        ? formProductId === productId
        : Boolean(idField && variantMap.has(Number(idField.value || 0)));
    });

    function formScore(form) {
      let score = 0;
      const rect = form.getBoundingClientRect();
      const style = window.getComputedStyle(form);
      const hiddenParent = form.closest('[aria-hidden="true"], [hidden]');
      const popupParent = form.closest('.cc-popup, .quickbuy-modal, .quickbuy-container, [data-quickbuy-modal]');
      const inMainContent = Boolean(form.closest('#content'));

      if (inMainContent) score += 100;
      if (!hiddenParent) score += 40;
      if (style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0) score += 30;
      if (popupParent) score -= 60;

      return score;
    }

    productForm = (matching.length ? matching : candidates)
      .sort((a, b) => formScore(b) - formScore(a))[0] || null;

    originalButton = productForm
      ? productForm.querySelector('.quantity-submit-row__submit button[type="submit"], button[type="submit"][data-add-to-cart-text], button[type="submit"]')
      : null;
    originalSelector = productForm
      ? productForm.querySelector('select.original-selector[name="id"], select[name="id"], input[name="id"]')
      : null;

    observeForm();
  }

  function observeForm() {
    if (formObserver) formObserver.disconnect();
    if (!productForm) return;

    // PDP apps can trigger hundreds of form mutations during hydration.
    // Coalesce them instead of re-reading product prices/options every 20ms.
    formObserver = new MutationObserver(() => scheduleSync(110));
    formObserver.observe(productForm.closest('.product-form') || productForm, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        'class',
        'disabled',
        'aria-disabled',
        'value',
        'selected',
        'checked',
        'required',
        'aria-required',
        'hidden',
        'style'
      ]
    });
  }

  function selectedVariant() {
    const value = originalSelector ? Number(originalSelector.value || 0) : 0;
    if (value && variantMap.has(value)) return variantMap.get(value);

    const stickyValue = variantSelect ? Number(variantSelect.value || 0) : 0;
    if (applyingStickyVariant && stickyValue && variantMap.has(stickyValue)) return variantMap.get(stickyValue);

    const urlVariant = Number(new URL(window.location.href).searchParams.get('variant') || 0);
    if (urlVariant && variantMap.has(urlVariant)) return variantMap.get(urlVariant);

    if (productData.hasOnlyDefaultVariant && variants.length) return variants[0];
    return null;
  }

  function readDisplayedPrice(selector, fallback) {
    const scope = productForm && productForm.closest('.product-form');
    if (!scope) return fallback;

    const nodes = scope.querySelectorAll(selector);
    const visible = visibleElement(nodes);
    const text = visible ? visible.textContent.trim() : '';
    return text || fallback;
  }

  function isSoldOut(variant) {
    if (variant && variant.available === false) return true;
    if (!originalButton) return false;

    const text = String(originalButton.textContent || '').toLowerCase();
    return originalButton.classList.contains('button--sold-out')
      || /sold out|out of stock|no stock|unavailable/.test(text);
  }

  function productFormScope() {
    return productForm
      ? (productForm.closest('.product-form') || productForm)
      : null;
  }

  function controlIsVisiblyActive(control) {
    if (!(control instanceof Element)) return false;
    if (control.disabled || control.getAttribute('aria-disabled') === 'true') return false;
    if (control.type === 'hidden' || control.hidden) return false;
    if (control.closest('[hidden], [aria-hidden="true"]')) return false;

    const style = window.getComputedStyle(control);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    // Off-screen controls still have client rects. A zero-rect control is inside
    // an inactive/hidden engraving or custom-option container.
    return control.getClientRects().length > 0;
  }

  function associatedRequiredControls() {
    if (!productForm) return [];

    const controls = new Set();

    // HTMLFormElement.elements includes controls associated through form="...",
    // even when the engraving field is rendered outside the <form> element.
    Array.from(productForm.elements || []).forEach((control) => {
      if (control instanceof Element) controls.add(control);
    });

    const scope = productFormScope();
    if (scope) {
      scope.querySelectorAll('[required], [aria-required="true"]').forEach((control) => {
        controls.add(control);
      });
    }

    return Array.from(controls).filter((control) => (
      control.required || control.getAttribute('aria-required') === 'true'
    ));
  }

  function requiredControlIsIncomplete(control) {
    if (!controlIsVisiblyActive(control)) return false;

    const type = String(control.type || '').toLowerCase();

    if (type === 'radio') {
      const name = control.name;
      if (!name) return !control.checked;

      const group = associatedRequiredControls().filter((candidate) => (
        String(candidate.type || '').toLowerCase() === 'radio'
        && candidate.name === name
        && controlIsVisiblyActive(candidate)
      ));

      return !group.some((candidate) => candidate.checked);
    }

    if (type === 'checkbox') return !control.checked;
    if (type === 'file') return !control.files || control.files.length === 0;

    if (control.tagName === 'SELECT') {
      return String(control.value || '').trim() === '';
    }

    if ('value' in control) {
      return String(control.value || '').trim() === '';
    }

    return Boolean(control.validity && control.validity.valueMissing);
  }

  function firstIncompleteRequiredControl() {
    return associatedRequiredControls().find(requiredControlIsIncomplete) || null;
  }

  function hasIncompleteRequiredFields() {
    return Boolean(firstIncompleteRequiredControl());
  }

  function eventBelongsToProductForm(target) {
    if (!(target instanceof Element) || !productForm) return false;
    if (target.form === productForm) return true;

    const scope = productFormScope();
    return Boolean(scope && scope.contains(target));
  }

  function needsOptions(variant) {
    if (!variant && !productData.hasOnlyDefaultVariant) return true;
    if (!originalButton) return true;
    if (isSoldOut(variant)) return false;

    if (hasIncompleteRequiredFields()) return true;

    const disabled = originalButton.disabled || originalButton.getAttribute('aria-disabled') === 'true';
    return Boolean(disabled);
  }

  // Symmetry temporarily disables its original product button during an AJAX
  // add, and keeps it disabled briefly after success. That is NOT an indication
  // that customers need to customise an already selected variant.
  function nativeAddFeedbackState() {
    if (!originalButton) return '';
    if (originalButton.querySelector('.loading-spinner')) return 'adding';
    if (originalButton.classList.contains('btn--added')) return 'added';
    return '';
  }

  function currentState() {
    const variant = selectedVariant();
    if (isSoldOut(variant)) return 'sold-out';

    // Preserve explicit required-field checks; show native add feedback only
    // when a genuine, available variant and all required options exist.
    const nativeFeedback = nativeAddFeedbackState();
    if (nativeFeedback && variant && !hasIncompleteRequiredFields()) return nativeFeedback;

    if (needsOptions(variant)) return 'select-options';
    return 'ready';
  }

  function setButtonState(state) {
    const isCartSyncing = document.documentElement.classList.contains('mmx-cart-syncing');
    const loading = state === 'adding' || (stickyRequestedAdd && (isCartSyncing || Date.now() < stickyPendingUntil));
    const addLabel = root.getAttribute('data-add-label') || 'ADD TO CART';
    const selectLabel = root.getAttribute('data-select-label') || 'SELECT OPTIONS';
    const soldLabel = root.getAttribute('data-sold-label') || 'SOLD OUT';
    const isPreorder = boolAttr('data-is-preorder', false);
    const originalReadyLabel = originalButton
      ? String(originalButton.getAttribute('data-add-to-cart-text') || '').trim()
      : '';

    let label = isPreorder ? (originalReadyLabel || 'PRE-ORDER') : addLabel;
    let disabled = false;

    if (state === 'sold-out') {
      label = soldLabel;
      disabled = true;
    } else if (state === 'select-options') {
      label = selectLabel;
    } else if (state === 'added') {
      label = 'ADDED TO CART';
      disabled = true;
    } else if (state === 'adding') {
      disabled = true;
    }

    if (loading) label = 'ADDING…';

    // Compact visual labels apply ONLY to normal mobile ADD / ADDED states;
    // desktop and pre-order/custom labels stay exactly as configured.
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const normalAdd = !isPreorder && addLabel.trim().toUpperCase() === 'ADD TO CART';
    const compactAdd = mobile && normalAdd && state === 'ready' && !loading;
    const visualLabel = compactAdd ? 'ADD' : (mobile && state === 'added' && !loading ? 'ADDED' : label);
    const accessibleLabel = compactAdd ? 'Add to cart'
      : (mobile && state === 'added' && !loading ? 'Added to cart'
      : (loading ? 'Adding to cart' : label));

    // Avoid invalidating styles/layout for identical observer-driven states.
    const nextState = loading ? 'adding' : state;
    const nextCompact = String(compactAdd);
    const nextDisabled = disabled || loading;
    const nextAriaDisabled = String(nextDisabled);
    const nextStatus = loading ? 'Adding product to cart' : (state === 'added' ? 'Added to cart' : '');
    if (buttonText.textContent !== visualLabel) buttonText.textContent = visualLabel;
    if (button.getAttribute('aria-label') !== accessibleLabel) button.setAttribute('aria-label', accessibleLabel);
    if (button.dataset.mmxState !== nextState) button.dataset.mmxState = nextState;
    if (button.dataset.mmxCompactAdd !== nextCompact) button.dataset.mmxCompactAdd = nextCompact;
    if (button.disabled !== nextDisabled) button.disabled = nextDisabled;
    if (button.getAttribute('aria-disabled') !== nextAriaDisabled) button.setAttribute('aria-disabled', nextAriaDisabled);
    if (button.classList.contains('is-loading') !== loading) button.classList.toggle('is-loading', loading);
    if (statusNode.textContent !== nextStatus) statusNode.textContent = nextStatus;

    if (stickyRequestedAdd && wasCartSyncing && !isCartSyncing) {
      stickyRequestedAdd = false;
      stickyPendingUntil = 0;
    }
    wasCartSyncing = isCartSyncing;
  }

  function syncProductDetails() {
    if (!productForm || !document.documentElement.contains(productForm)) {
      findProductForm();
    }

    const variant = selectedVariant();
    const fallbackPrice = variant ? formatMoney(variant.price) : '';
    const fallbackCompare = variant && Number(variant.compareAtPrice) > Number(variant.price)
      ? formatMoney(variant.compareAtPrice)
      : '';
    const useForcedVariantPrice = Date.now() < forceVariantPriceUntil;

    const nextPrice = useForcedVariantPrice
      ? fallbackPrice
      : readDisplayedPrice(
          '.variant-visibility-area > .price-area .current-price, .price-container .price-area .current-price, .price-area .current-price',
          fallbackPrice
        );
    if (priceNode.textContent !== nextPrice) priceNode.textContent = nextPrice;

    const compareText = useForcedVariantPrice
      ? fallbackCompare
      : readDisplayedPrice(
          '.variant-visibility-area > .price-area .was-price, .price-container .price-area .was-price, .price-area .was-price',
          fallbackCompare
        );
    const showCompare = boolAttr('data-show-compare', true)
      && Boolean(compareText)
      && compareText !== priceNode.textContent;

    if (compareNode.textContent !== compareText) compareNode.textContent = compareText;
    if (compareNode.hidden !== !showCompare) compareNode.hidden = !showCompare;

    if (variantControl) {
      const shouldHideVariant = Boolean(productData.hasOnlyDefaultVariant || variants.length <= 1);
      if (variantControl.hidden !== shouldHideVariant) variantControl.hidden = shouldHideVariant;
    }

    if (variantSelect && !applyingStickyVariant) {
      const nextValue = variant ? String(variant.id) : '';
      if (variantSelect.value !== nextValue) variantSelect.value = nextValue;
      const isUnavailable = Boolean(variant && variant.available === false);
      if (variantSelect.classList.contains('is-unavailable') !== isUnavailable) {
        variantSelect.classList.toggle('is-unavailable', isUnavailable);
      }
    }

    // Only the closed mobile selector uses the compact title. The real native
    // <select> retains full variant + price options when opened on iOS/Android.
    if (variantShortNode && variantSelect) {
      const option = variantSelect.selectedOptions && variantSelect.selectedOptions[0];
      const nextTitle = option
        ? (option.getAttribute('data-short-title') || option.textContent.trim())
        : 'Select variant';
      if (variantShortNode.textContent !== nextTitle) variantShortNode.textContent = nextTitle;
    }

    if (imageNode && boolAttr('data-show-image', true)) {
      const imageUrl = variant && variant.image;
      if (imageUrl && imageNode.getAttribute('src') !== imageUrl) imageNode.setAttribute('src', imageUrl);
    }

    const state = currentState();
    setButtonState(state);
    lastState = state;
  }

  function controlGroups(scope) {
    const groups = [];
    const byName = new Map();

    scope.querySelectorAll('select[name^="options["], input[name^="options["]').forEach((control) => {
      const name = control.getAttribute('name') || '';
      if (!byName.has(name)) {
        const group = { name, controls: [] };
        byName.set(name, group);
        groups.push(group);
      }
      byName.get(name).controls.push(control);
    });

    return groups;
  }

  function setStandardOptionControls(scope, variant) {
    const values = Array.isArray(variant.options) ? variant.options : [];
    if (!values.length) return false;

    const groups = controlGroups(scope);
    let changed = false;

    values.forEach((value, index) => {
      const expectedName = optionNames[index] ? `options[${optionNames[index]}]` : '';
      const group = groups.find((candidate) => candidate.name === expectedName) || groups[index];
      if (!group) return;

      const select = group.controls.find((control) => control.tagName === 'SELECT');
      if (select) {
        const matchingOption = Array.from(select.options).find((option) => option.value === String(value));
        if (matchingOption && select.value !== String(value)) {
          select.value = String(value);
          dispatchInputAndChange(select);
          changed = true;
        }
        return;
      }

      const input = group.controls.find((control) => (
        (control.type === 'radio' || control.type === 'checkbox')
        && control.value === String(value)
      ));

      if (input && !input.checked) {
        input.click();
        changed = true;
      }
    });

    return changed;
  }

  function normaliseOptionValue(value) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function symmetryOptionGroups(scope) {
    const selectors = [
      '.option-selectors .option-selector',
      '.option-selectors > *[data-option-name]',
      '.variant-picker .option-selector'
    ];

    const groups = [];
    selectors.forEach((selector) => {
      scope.querySelectorAll(selector).forEach((group) => {
        if (!groups.includes(group)) groups.push(group);
      });
    });

    return groups;
  }

  function matchingSymmetryControl(group, value) {
    if (!group) return null;

    const target = normaliseOptionValue(value);
    const controls = Array.from(group.querySelectorAll(
      'input.opt-btn.js-option, input.js-option, input.opt-btn, '
      + 'select.js-option, select[name^="options["], input[name^="options["]'
    ));

    let control = controls.find((candidate) => (
      normaliseOptionValue(candidate.value) === target
    ));

    if (control) return control;

    const labels = Array.from(group.querySelectorAll('label, .opt-label'));
    const label = labels.find((candidate) => (
      normaliseOptionValue(candidate.textContent) === target
    ));

    if (!label) return null;

    if (label.htmlFor) {
      control = document.getElementById(label.htmlFor);
      if (control) return control;
    }

    return label.querySelector('input, select') || label.previousElementSibling || label;
  }

  function activateSymmetryControl(control, value) {
    if (!control) return false;

    if (control.tagName === 'SELECT') {
      const matchingOption = Array.from(control.options || []).find((option) => (
        normaliseOptionValue(option.value) === normaliseOptionValue(value)
        || normaliseOptionValue(option.textContent) === normaliseOptionValue(value)
      ));

      if (!matchingOption || control.value === matchingOption.value) return false;

      control.value = matchingOption.value;
      dispatchInputAndChange(control);
      return true;
    }

    if (control.matches('input[type="radio"], input[type="checkbox"]')) {
      if (control.checked) return false;

      // Symmetry listens to click events on .opt-btn.js-option. Using click()
      // updates the visible selected state and runs the theme's variant logic.
      control.click();

      if (!control.checked) {
        control.checked = true;
        dispatchInputAndChange(control);
      }

      return true;
    }

    if (control.matches('label, .opt-label')) {
      control.click();
      return true;
    }

    if (typeof control.click === 'function') {
      control.click();
      return true;
    }

    return false;
  }

  function setSymmetryOptionControls(scope, variant) {
    const values = Array.isArray(variant.options) ? variant.options : [];
    if (!values.length) return false;

    const groups = symmetryOptionGroups(scope);
    if (!groups.length) return false;

    let changed = false;

    values.forEach((value, index) => {
      const group = groups[index];
      const control = matchingSymmetryControl(group, value);
      if (activateSymmetryControl(control, value)) changed = true;
    });

    return changed;
  }

  function setGoldBarVariantControls(scope, variantId) {
    let changed = false;

    scope.querySelectorAll('.variant-selector-gold-bar').forEach((select) => {
      const matchingOption = Array.from(select.options || []).find((option) => Number(option.value || 0) === variantId);
      if (!matchingOption || Number(select.value || 0) === variantId) return;

      select.value = String(variantId);
      dispatchInputAndChange(select);
      changed = true;
    });

    return changed;
  }

  function commitOriginalVariant(variantId) {
    if (!originalSelector) return;

    const matchingOption = originalSelector.tagName === 'SELECT'
      ? Array.from(originalSelector.options || []).find((option) => Number(option.value || 0) === variantId)
      : true;

    if (!matchingOption) return;

    originalSelector.value = String(variantId);
    dispatchInputAndChange(originalSelector);
  }

  function applyStickyVariant(variantId) {
    const variant = variantMap.get(Number(variantId || 0));
    if (!variant || !productForm) {
      if (originalSelector) {
        originalSelector.value = '';
        dispatchInputAndChange(originalSelector);
      }
      scheduleSync();
      return;
    }

    const scope = productForm.closest('.product-form') || productForm;
    applyingStickyVariant = true;
    forceVariantPriceUntil = Date.now() + 1400;

    setStandardOptionControls(scope, variant);
    setSymmetryOptionControls(scope, variant);
    setGoldBarVariantControls(scope, Number(variant.id));

    window.requestAnimationFrame(() => {
      // Run another pass after Symmetry has processed the first option click.
      // This is important when a product has separate Size and Weight controls.
      setSymmetryOptionControls(scope, variant);
      commitOriginalVariant(Number(variant.id));

      window.setTimeout(() => {
        setStandardOptionControls(scope, variant);
        setSymmetryOptionControls(scope, variant);
        setGoldBarVariantControls(scope, Number(variant.id));
        commitOriginalVariant(Number(variant.id));
        scheduleSync();
      }, 90);

      window.setTimeout(() => {
        setSymmetryOptionControls(scope, variant);
        commitOriginalVariant(Number(variant.id));
        applyingStickyVariant = false;
        scheduleSync();
      }, 260);

      window.setTimeout(scheduleSync, 520);
      window.setTimeout(scheduleSync, 1000);
    });

    track('mmx_sticky_atc_variant_change', {
      variant_id: Number(variant.id),
      variant_title: variant.title || ''
    });
  }

  function singaporeToday() {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Singapore',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date());

      const values = {};
      parts.forEach((part) => {
        if (part.type !== 'literal') values[part.type] = Number(part.value);
      });

      return new Date(values.year, values.month - 1, values.day, 12, 0, 0);
    } catch (error) {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
    }
  }

  function addBusinessDays(startDate, businessDays) {
    const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 12, 0, 0);
    let remaining = Math.max(0, Number(businessDays || 0));

    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) remaining -= 1;
    }

    return date;
  }

  function shortMonth(date) {
    return new Intl.DateTimeFormat('en-SG', { month: 'short', timeZone: 'Asia/Singapore' }).format(date);
  }

  function formatShortRange(minDate, maxDate) {
    const sameMonth = minDate.getMonth() === maxDate.getMonth() && minDate.getFullYear() === maxDate.getFullYear();
    if (sameMonth) return `${minDate.getDate()}–${maxDate.getDate()} ${shortMonth(maxDate)}`;
    return `${minDate.getDate()} ${shortMonth(minDate)}–${maxDate.getDate()} ${shortMonth(maxDate)}`;
  }

  function deliveryConfig() {
    const node = Array.from(document.querySelectorAll('script#MMXSpendTierConfig')).pop();
    if (node) {
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        if (parsed.delivery) return parsed.delivery;
      } catch (error) {
        console.warn('[MMX Sticky ATC] Unable to read cart delivery settings.', error);
      }
    }

    return {
      enabled: true,
      standardMinDays: 3,
      standardMaxDays: 5,
      priorityMinDays: 1,
      priorityMaxDays: 3
    };
  }

  function updateDelivery() {
    if (!deliveryNode || !boolAttr('data-show-delivery', true)) {
      if (deliveryNode) deliveryNode.textContent = '';
      return;
    }

    const state = currentState();
    if (state === 'sold-out') {
      deliveryNode.textContent = '';
      return;
    }

    const isPreorder = boolAttr('data-is-preorder', false);
    const preorderNote = String(root.getAttribute('data-preorder-note') || '').trim();
    if (isPreorder) {
      deliveryNode.textContent = preorderNote ? `Pre-order: ${preorderNote}` : 'Pre-order item';
      return;
    }

    const config = deliveryConfig();
    if (config.enabled === false) {
      deliveryNode.textContent = '';
      return;
    }

    const today = singaporeToday();
    const standard = formatShortRange(
      addBusinessDays(today, config.standardMinDays),
      addBusinessDays(today, config.standardMaxDays)
    );
    const priority = formatShortRange(
      addBusinessDays(today, config.priorityMinDays),
      addBusinessDays(today, config.priorityMaxDays)
    );

    deliveryNode.textContent = `Shipping/Collection: ${standard.replace('–', ' - ')} · Priority Shipping: ${priority.replace('–', ' - ')}`;
  }

  function drawerIsOpen() {
    if (document.documentElement.classList.contains('mmx-cart-drawer-open')) return true;
    // Support the existing Symmetry drawer before MMX custom cart is restored.
    const drawers = document.querySelectorAll('.cart-drawer-modal.cc-popup');
    return Array.from(drawers).some((drawer) => (
      drawer.getAttribute('aria-hidden') === 'false'
      || drawer.classList.contains('is-open')
      || drawer.classList.contains('cc-popup--visible')
    ));
  }

  function breakpointEnabled() {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    return mobile
      ? root.classList.contains('mmx-sticky-atc--mobile-enabled')
      : root.classList.contains('mmx-sticky-atc--desktop-enabled');
  }

  function shouldShow() {
    if (!breakpointEnabled() || !productForm || !originalButton || drawerIsOpen()) return false;

    const rect = originalButton.getBoundingClientRect();
    const hasPassedOriginalButton = rect.bottom <= 0;
    const originalButtonVisible = rect.bottom > 0 && rect.top < window.innerHeight;

    return hasPassedOriginalButton && !originalButtonVisible;
  }

  function track(eventName, detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({
      event: eventName,
      product_id: productId,
      product_handle: root.getAttribute('data-product-handle') || ''
    }, detail || {}));
  }

  function topLightDomHost(element) {
    let target = element;
    let rootNode = target && target.getRootNode ? target.getRootNode() : document;

    while (rootNode && rootNode !== document && rootNode.host) {
      target = rootNode.host;
      rootNode = target.getRootNode ? target.getRootNode() : document;
    }

    return target instanceof Element ? target : null;
  }

  function externalFixedAncestor(element) {
    let current = topLightDomHost(element);

    while (current && current instanceof Element && current !== document.body) {
      if (current === root || current.closest('[data-mmx-sticky-atc]')) return null;

      const style = window.getComputedStyle(current);
      if (style.position === 'fixed') return current;

      if (current.parentElement) {
        current = current.parentElement;
      } else {
        const currentRoot = current.getRootNode ? current.getRootNode() : null;
        current = currentRoot && currentRoot.host ? currentRoot.host : null;
      }
    }

    return null;
  }

  function widgetOverlapsBar(widget) {
    const barRect = root.getBoundingClientRect();
    const widgetRect = widget.getBoundingClientRect();

    return widgetRect.right > barRect.left
      && widgetRect.left < barRect.right
      && widgetRect.bottom > barRect.top
      && widgetRect.top < barRect.bottom;
  }

  function markWidgetBelowBar(widget) {
    const target = topLightDomHost(widget);
    if (!target || target === root || target.closest('[data-mmx-sticky-atc]')) return;

    target.setAttribute('data-mmx-below-sticky-atc', 'true');
    loweredWidgets.add(target);
  }

  function restoreWidgetLayers() {
    loweredWidgets.forEach((node) => {
      if (node && node.removeAttribute) node.removeAttribute('data-mmx-below-sticky-atc');
    });
    loweredWidgets.clear();
  }

  function lowerOverlappingFloatingWidgets() {
    if (!root.classList.contains('is-visible') || !document.elementsFromPoint) {
      restoreWidgetLayers();
      return;
    }

    const rect = root.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const xValues = [
      rect.right - 18,
      rect.right - 55,
      rect.right - 95,
      rect.right - Math.min(145, rect.width * 0.25)
    ].map((value) => Math.max(1, Math.min(window.innerWidth - 1, value)));

    const yValues = [
      rect.bottom - 18,
      rect.bottom - Math.min(55, rect.height * 0.35),
      rect.top + Math.min(35, rect.height * 0.25),
      rect.top + Math.min(85, rect.height * 0.65)
    ].map((value) => Math.max(1, Math.min(window.innerHeight - 1, value)));

    xValues.forEach((x) => {
      yValues.forEach((y) => {
        const stack = document.elementsFromPoint(x, y);

        for (const element of stack) {
          if (!(element instanceof Element) || element === root || element.closest('[data-mmx-sticky-atc]')) continue;

          const fixed = externalFixedAncestor(element);
          if (!fixed || !widgetOverlapsBar(fixed)) continue;

          const widgetRect = fixed.getBoundingClientRect();
          const isFloatingControl = widgetRect.width >= 20
            && widgetRect.height >= 20
            && widgetRect.width <= 380
            && widgetRect.height <= 380;

          if (isFloatingControl) {
            markWidgetBelowBar(fixed);
            break;
          }
        }
      });
    });
  }

  function scheduleWidgetLayerSync() {
    window.clearTimeout(widgetLayerTimer);
    widgetLayerTimer = window.setTimeout(lowerOverlappingFloatingWidgets, 0);

    widgetLayerFollowups.forEach((timer) => window.clearTimeout(timer));
    widgetLayerFollowups = [80, 320, 900].map((delay) => (
      window.setTimeout(lowerOverlappingFloatingWidgets, delay)
    ));
  }

  function updateVisibility() {
    const wasVisible = root.classList.contains('is-visible');
    const show = shouldShow();
    // Prevent unnecessary class mutations and observer-triggered sync work.
    if (wasVisible !== show) root.classList.toggle('is-visible', show);
    if (document.documentElement.classList.contains('mmx-sticky-atc-visible') !== show) {
      document.documentElement.classList.toggle('mmx-sticky-atc-visible', show);
    }

    const nextAriaHidden = String(!show);
    if (root.getAttribute('aria-hidden') !== nextAriaHidden) {
      root.setAttribute('aria-hidden', nextAriaHidden);
    }

    // The bar's visible state usually stays unchanged while scrolling. Avoid
    // expensive floating-widget hit testing on every scroll event.
    if (show && !wasVisible) {
      scheduleWidgetLayerSync();
    } else if (!show && wasVisible) {
      restoreWidgetLayers();
    }

    if (show && !hasTrackedImpression) {
      hasTrackedImpression = true;
      track('mmx_sticky_atc_impression', { state: lastState || currentState() });
    }
  }

  function scheduleVisibility() {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = 0;
      updateVisibility();
    });
  }

  function scheduleSync(delay) {
    const wait = delay == null ? 20 : Math.max(0, Number(delay) || 0);
    const due = Date.now() + wait;
    // A short user-initiated sync can supersede a pending hydration sync,
    // but repeated MutationObserver notifications cannot keep restarting it.
    if (syncTimer && nextSyncDue <= due) return;
    if (syncTimer) window.clearTimeout(syncTimer);
    nextSyncDue = due;
    syncTimer = window.setTimeout(() => {
      syncTimer = 0;
      nextSyncDue = 0;
      syncProductDetails();
      updateDelivery();
      scheduleVisibility();
    }, wait);
  }

  function scrollToOptions() {
    if (!productForm) return;

    const scope = productFormScope() || productForm;
    const incompleteControl = firstIncompleteRequiredControl();

    if (incompleteControl) {
      const target = incompleteControl.closest(
        '.engraving-input-container, '
        + '.line-item-property__field, '
        + '.custom-option, '
        + '.option-selector, '
        + '.input-row'
      ) || incompleteControl;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      window.setTimeout(() => {
        if (typeof incompleteControl.focus === 'function') {
          incompleteControl.focus({ preventScroll: true });
        }
      }, 350);

      return;
    }

    const candidates = [
      '.option-selector',
      '.variant-selector',
      '.variant-picker',
      '.variant-selector-gold-bar',
      '.quantity-selector-gold-bar',
      '.engraving-input:required',
      '.input-row'
    ];

    let target = null;
    for (const selector of candidates) {
      target = visibleElement(scope.querySelectorAll(selector));
      if (target) break;
    }

    target = target || scope;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    window.setTimeout(() => {
      const focusable = target.matches('input, select, button, textarea, [tabindex]')
        ? target
        : target.querySelector('input:not([type="hidden"]), select, button, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable && typeof focusable.focus === 'function') focusable.focus({ preventScroll: true });
    }, 350);
  }

  if (variantSelect) {
    variantSelect.addEventListener('change', () => {
      // Update the visual title immediately; PDP synchronisation follows.
      if (variantShortNode) {
        const option = variantSelect.selectedOptions && variantSelect.selectedOptions[0];
        variantShortNode.textContent = option
          ? (option.getAttribute('data-short-title') || option.textContent.trim())
          : 'Select variant';
      }
      applyStickyVariant(Number(variantSelect.value || 0));
    });
  }

  button.addEventListener('click', () => {
    const state = currentState();
    track('mmx_sticky_atc_click', { state });

    if (state === 'sold-out') return;

    if (state === 'select-options') {
      scrollToOptions();
      return;
    }

    if (!originalButton || originalButton.disabled || originalButton.getAttribute('aria-disabled') === 'true') {
      scrollToOptions();
      return;
    }

    stickyRequestedAdd = true;
    stickyPendingUntil = Date.now() + 1200;
    setButtonState(state);
    originalButton.click();

    window.setTimeout(() => {
      if (!document.documentElement.classList.contains('mmx-cart-syncing')) {
        stickyRequestedAdd = false;
        stickyPendingUntil = 0;
        setButtonState(currentState());
      }
    }, 900);
  });

  document.addEventListener('change', (event) => {
    if (event.target !== variantSelect && eventBelongsToProductForm(event.target)) {
      scheduleSync();
    }
  }, true);

  document.addEventListener('input', (event) => {
    if (event.target !== variantSelect && eventBelongsToProductForm(event.target)) {
      scheduleSync();
    }
  }, true);

  document.addEventListener('theme:cartchanged', scheduleSync);
  document.addEventListener('theme:open-cart-drawer', () => { scheduleVisibility(); window.setTimeout(scheduleVisibility, 80); });
  document.addEventListener('theme:close-cart-drawer', () => { scheduleVisibility(); window.setTimeout(scheduleVisibility, 80); });
  document.addEventListener('shopify:section:load', () => {
    findProductForm();
    scheduleSync();
  });
  document.addEventListener('shopify:section:unload', scheduleSync);

  window.addEventListener('scroll', scheduleVisibility, { passive: true });
  window.addEventListener('resize', () => {
    scheduleSync();
    scheduleWidgetLayerSync();
  }, { passive: true });
  window.addEventListener('pageshow', scheduleSync);

  // Observe only the document state and the existing cart drawer instead of
  // every class/aria mutation in the entire DOM (which is expensive on mobile).
  // Most <html> class changes come from unrelated apps (and from our own
  // visibility class). Re-sync only when the cart-related state actually changes.
  let watchedDocumentState = [
    document.documentElement.classList.contains('mmx-cart-syncing'),
    document.documentElement.classList.contains('mmx-cart-drawer-open')
  ];
  documentObserver = new MutationObserver((records) => {
    const nextDocumentState = [
      document.documentElement.classList.contains('mmx-cart-syncing'),
      document.documentElement.classList.contains('mmx-cart-drawer-open')
    ];
    const documentStateChanged = nextDocumentState.some((value, index) => value !== watchedDocumentState[index]);
    watchedDocumentState = nextDocumentState;
    const drawerChanged = records.some((record) => record.target !== document.documentElement);
    if (documentStateChanged || drawerChanged) scheduleSync(80);
  });
  documentObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  });
  const symmetryDrawer = document.querySelector('.cart-drawer-modal.cc-popup');
  if (symmetryDrawer) {
    documentObserver.observe(symmetryDrawer, {
      attributes: true,
      attributeFilter: ['class', 'aria-hidden']
    });
  }

  root.hidden = false;
  findProductForm();
  syncProductDetails();
  // CSS activates the overlay only after JS is ready; if JS fails, the native
  // selector remains legible instead of exposing an empty visual control.
  root.dataset.mmxCompactReady = 'true';
  updateDelivery();
  updateVisibility();

  // One-time follow-ups cover deferred Symmetry PDP hydration. Subsequent
  // updates are driven by actual form mutations, selection and theme events.
  window.setTimeout(scheduleSync, 300);
  window.setTimeout(scheduleSync, 1800);
}());
